import { useState } from "react";
import { Link } from "react-router-dom";
import AbschlussPin from "../AbschlussPin";
import AngebotSenden from "./AngebotSenden";
import Kundenlink from "./Kundenlink";
import {
  useZustimmungErstellen, useAktivieren,
  useVertragPruefung, datum, eur, VERTRAG_STATUS_LABEL,
} from "../../hooks/useVertraege";
import type { VertragDetail } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";
import { datumDe } from "../../lib/zahl";
import { zuSektion } from "./Sektion";

/** Das Phasenband — sechs Stationen eines Pflegevertrags. Muster: Kontors Projektkopf. */
const PHASEN = [
  { key: "entwurf", name: "Entwurf" },
  { key: "angeboten", name: "Angebot" },
  { key: "zugestimmt", name: "Zugestimmt" },
  { key: "aktiv", name: "Läuft" },
  { key: "gekuendigt", name: "Kündigung" },
  { key: "beendet", name: "Beendet" },
];

function phasenIndex(status: string): number {
  if (status === "pausiert") return 3;
  const i = PHASEN.findIndex(p => p.key === status);
  return i < 0 ? 0 : i;
}

type Fakt = { was: string; wert: string; marke?: { text: string; ton: "gruen" | "gelb" | "rot" } };

/**
 * Karte „Jetzt dran" — was an diesem Vertrag als Nächstes zu tun ist.
 *
 * Ersetzt seit 09.10.2026 die Karten „Vertragsabschluss" und „Angebot liegt
 * beim Kunden". Aufbau wie in Kontor (`KopfJetzt`): Phasenband, Phase mit
 * einem Satz Grund, ein Hauptknopf, höchstens ein Nebenknopf, rechts was
 * gerade zählt. „Mensch im Loop": nichts passiert ohne Klick.
 */
export default function JetztDran({ v }: { v: VertragDetail }) {
  const erstellen = useZustimmungErstellen(v.id);
  // Nur solange es ein Entwurf ist: danach ist die Prüfung durch.
  const { data: pruefung } = useVertragPruefung(v.id, v.status === "entwurf");
  const [mailVorschau, setMailVorschau] = useState(false);
  const aktivieren = useAktivieren(v.id);
  const [msg, setMsg] = useState("");
  const [fehler, setFehler] = useState("");
  const z = v.zustimmung;
  const idx = phasenIndex(v.status);
  const abgelehnt = v.status === "angeboten" && !!z?.abgelehnt_am;
  const abgelaufen = !!z?.gueltig_bis && new Date(z.gueltig_bis) < new Date();

  async function run(fn: () => Promise<unknown>, ok: string) {
    setMsg(""); setFehler("");
    try { await fn(); if (ok) setMsg(ok); } catch (e) { setFehler(fehlertext(e) || "Aktion fehlgeschlagen."); }
  }

  let phase = PHASEN[idx].name;
  let grund = "";
  let haupt: JSX.Element | null = null;
  let neben: JSX.Element | null = null;
  let faktenTitel = "Vertrag";
  const fakten: Fakt[] = [];

  if (v.status === "entwurf") {
    phase = "Angebot vorbereiten";
    grund = pruefung && !pruefung.ok
      ? `Dafür fehlt noch ${pruefung.fehler.length === 1 ? "eine Sache" : `${pruefung.fehler.length} Sachen`} — siehe unten.`
      : "Mit dem Senden bekommt der Vertrag seine Nummer. Der Kunde bekommt seinen Kundenlink und stimmt dort online oder vor Ort mit der PIN zu.";
    haupt = (
      <button type="button" className="pk-knopf vg-btn vg-btn-primary"
        disabled={erstellen.isPending || (pruefung ? !pruefung.ok : false)}
        onClick={() => run(async () => { await erstellen.mutateAsync(); setMailVorschau(true); }, "")}>
        {erstellen.isPending ? "Bereite vor…" : "Angebot senden …"}
      </button>
    );
    neben = <Link className="pk-knopf vg-btn" to={`/vertraege/neu?entwurf=${v.id}`}>Weiterbearbeiten</Link>;
    faktenTitel = "Angebot";
    fakten.push({ was: "Leistungen", wert: String(v.positionen ?? v.leistungen.length) });
    fakten.push({ was: "Jahreswert", wert: `${eur(v.jahres_netto)} netto` });
    fakten.push({ was: "Beginn", wert: v.beginn ? datumDe(v.beginn) : "noch offen" });
  } else if (v.status === "angeboten") {
    phase = abgelehnt ? "Abgelehnt" : "Wartet auf Zustimmung";
    grund = abgelehnt
      ? `Der Kunde hat das Angebot am ${datum(z?.abgelehnt_am)} abgelehnt.`
      : abgelaufen
        ? `Der Link ist am ${datum(z?.gueltig_bis)} abgelaufen — erneut per E-Mail senden verlängert ihn um 30 Tage.`
        : "Änderungen am Angebot sieht der Kunde unter seinem Kundenlink sofort; ein neuer Link ist nicht nötig. Hat er die Seite gerade offen, lieber kurz anrufen.";
    if (!abgelehnt) {
      haupt = (
        <button type="button" className="pk-knopf vg-btn vg-btn-primary" onClick={() => setMailVorschau(true)}>
          {z?.gesendet_am ? "Erneut per E-Mail senden …" : "Per E-Mail senden …"}
        </button>
      );
    }
    neben = <Link className="pk-knopf vg-btn" to={`/vertraege/neu?entwurf=${v.id}`}>Angebot ändern</Link>;
    faktenTitel = "Worauf wir warten";
    fakten.push({ was: "Gesendet", wert: z?.gesendet_am ? datum(z.gesendet_am) : "noch nicht",
      marke: z?.gesendet_am ? undefined : { text: "offen", ton: "gelb" } });
    fakten.push({ was: "Geöffnet", wert: z?.geoeffnet_am ? datum(z.geoeffnet_am) : "noch nicht" });
    if (z?.gueltig_bis) fakten.push({ was: "Gültig bis", wert: datum(z.gueltig_bis),
      marke: abgelaufen ? { text: "abgelaufen", ton: "rot" } : undefined });
  } else if (v.status === "zugestimmt") {
    phase = "Aktivieren";
    grund = `Zugestimmt${z?.zugestimmt_am ? ` am ${datum(z.zugestimmt_am)}` : ""}${z?.zustimmer_name ? ` von ${z.zustimmer_name}` : ""}. `
      + "Eigentlich wird ein Vertrag mit der Zustimmung von selbst aktiv; hier hat das nicht geklappt "
      + "(der Grund steht in der Büromail). Mit der Aktivierung entstehen Pflegeprojekt, Raten und Jahres-Einsatzplan.";
    haupt = (
      <button type="button" className="pk-knopf vg-btn vg-btn-primary" disabled={aktivieren.isPending}
        onClick={() => run(() => aktivieren.mutateAsync(), "Vertrag aktiviert — Einsatzplan angelegt.")}>
        {aktivieren.isPending ? "Aktiviere…" : "Vertrag aktivieren"}
      </button>
    );
    faktenTitel = "Bereit";
    fakten.push({ was: "Ausfertigung", wert: v.ausfertigung ? "festgeschrieben" : "fehlt",
      marke: v.ausfertigung ? { text: "ok", ton: "gruen" } : { text: "nachholen", ton: "rot" } });
    fakten.push({ was: "Beginn", wert: v.beginn ? datumDe(v.beginn) : "—" });
  } else {
    phase = v.status === "aktiv" ? "Läuft" : (VERTRAG_STATUS_LABEL[v.status] ?? v.status);
    grund = (v.termine_generiert > 0
      ? `${v.termine_generiert} Termine im Jahresplan angelegt — im Planer prüfen und fixieren.`
      : "Einsätze werden im Planer disponiert.")
      + (z?.zugestimmt_am ? ` Zugestimmt am ${datum(z.zugestimmt_am)}${z.zustimmer_name ? ` von ${z.zustimmer_name}` : ""}.` : "");
    if (v.status === "aktiv") {
      neben = <button type="button" className="pk-knopf vg-btn" onClick={() => zuSektion("nachtrag")}>Nachtrag anlegen</button>;
    }
    if (v.status === "gekuendigt" || v.gekuendigt_zum) {
      haupt = <button type="button" className="pk-knopf vg-btn vg-btn-primary" onClick={() => zuSektion("abrechnung")}>Zur Schlussabrechnung</button>;
    }
    faktenTitel = "Vertragsjahr";
    fakten.push({ was: "Beginn", wert: v.beginn ? datumDe(v.beginn) : "—" });
    if (v.gekuendigt_zum) fakten.push({ was: "Endet", wert: datum(v.gekuendigt_zum), marke: { text: "gekündigt", ton: "rot" } });
    if (v.ist) fakten.push({ was: "Einsätze", wert: `${v.ist.einsaetze_erledigt} von ${v.ist.einsaetze_gesamt}` });
    if (v.preisanpassung_zum) fakten.push({ was: "Preis", wert: `Anpassung zum ${datum(v.preisanpassung_zum)}` });
  }

  return (
    <section className="pk-jetzt" aria-label="Jetzt dran">
      <ol className="pk-band" style={{ gridTemplateColumns: `repeat(${PHASEN.length}, minmax(0, 1fr))` }}>
        {PHASEN.map((p, i) => (
          <li key={p.key} className={"pk-band-schritt" + (i < idx ? " is-fertig" : i === idx ? " is-jetzt" : "")}
            aria-current={i === idx ? "step" : undefined}>
            <span className="pk-band-punkt" aria-hidden="true">{i < idx ? "✓" : ""}</span>
            {i < PHASEN.length - 1 && <span className="pk-band-linie" aria-hidden="true" />}
            <span className="pk-band-name">{p.name}</span>
          </li>
        ))}
      </ol>
      <div className="pk-band-kurz" aria-hidden="true">
        <div className="pk-band-segmente">
          {PHASEN.map((p, i) => <span key={p.key} className={i < idx ? "is-fertig" : i === idx ? "is-jetzt" : ""} />)}
        </div>
        <span>Schritt {idx + 1} von {PHASEN.length}</span>
      </div>

      <div className="pk-raster">
        <div className="pk-phase">
          <span className="pk-eyebrow">Jetzt dran</span>
          <h2 className="pk-phase-name">{phase}</h2>
          <p className="pk-grund">{grund}</p>

          {v.status === "entwurf" && pruefung && !pruefung.ok && (
            <ul className="pk-frage" style={{ display: "block", paddingLeft: 30, margin: 0 }}>
              {pruefung.fehler.map((f, i) => <li key={i} style={{ marginBottom: 3 }}>{f}</li>)}
            </ul>
          )}
          {v.status === "entwurf" && pruefung && pruefung.ok && pruefung.warnungen.length > 0 && (
            <p className="pk-meta">{pruefung.warnungen.join(" · ")}</p>
          )}

          {(haupt || neben) && <div className="pk-knoepfe">{haupt}{neben}</div>}

          {v.status === "angeboten" && <Kundenlink kundeId={v.kunde_id} />}
          {v.status === "angeboten" && z?.token && !abgelehnt && <AbschlussPin vertragId={v.id} />}

          {/* Erst zeigen, dann senden (Testlauf 06.10.2026). */}
          {mailVorschau && v.status === "angeboten" && (
            <AngebotSenden vertragId={v.id}
              onGesendet={an => { setMailVorschau(false); setMsg(`Angebot an ${an} gesendet.`); }}
              onAbbrechen={() => setMailVorschau(false)} />
          )}

          {msg && <p className="pk-hinweis">{msg}</p>}
          {fehler && <p className="pk-fehler">{fehler}</p>}
        </div>

        {fakten.length > 0 && (
          <div className="pk-fakten">
            <span className="pk-eyebrow">{faktenTitel}</span>
            {fakten.map(f => (
              <div key={f.was} className="pk-fakt">
                <span className="pk-fakt-was">{f.was}</span>
                <span className="pk-fakt-wert">{f.wert}</span>
                {f.marke && <span className={"pk-marke is-" + f.marke.ton}>{f.marke.text}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
